import { useState } from "react";
import { 
    CircleStackIcon, 
    ArrowPathIcon,
    CheckCircleIcon
} from "@heroicons/react/24/outline";
import { setOnboardingStorage } from "utils/onboardingStorage";

const OnboardingWebsite = (props: { onNext: any; onPrev: any }) => {
    const { onNext, onPrev } = props;

    const [selectedEnrichment, setSelectedEnrichment] = useState<string[]>([]);
    const [databaseType, setDatabaseType] = useState<string>("");

    const enrichmentTools = [
        { id: "hunter", label: "Hunter.io" },
        { id: "apollo", label: "Apollo.io" },
        { id: "kaspr", label: "Kaspr" },
        { id: "dropcontact", label: "Dropcontact" },
        { id: "lusha", label: "Lusha" },
        { id: "zoominfo", label: "ZoomInfo" },
        { id: "linkedin-sales", label: "LinkedIn Sales Navigator" },
        { id: "none", label: "Aucun pour le moment" },
        { id: "other", label: "Autre" }
    ];

    const toggleEnrichment = (id: string) => {
        if (id === "none") {
            setSelectedEnrichment(["none"]);
        } else {
            let newSelection = selectedEnrichment.filter(item => item !== "none");
            if (selectedEnrichment.includes(id)) {
                newSelection = newSelection.filter(item => item !== id);
            } else {
                newSelection = [...newSelection, id];
            }
            setSelectedEnrichment(newSelection);
        }
    };

    const handleContinue = () => {
        if (selectedEnrichment.length === 0) {
            alert("Veuillez sélectionner au moins un outil d'enrichissement");
            return;
        }
        if (!databaseType) {
            alert("Veuillez sélectionner un type de base de données");
            return;
        }
        setOnboardingStorage("enrichmentTools", selectedEnrichment.join(","));
        setOnboardingStorage("databaseType", databaseType);
        onNext();
    };

    return (
        <div className="flex flex-col items-center justify-between min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-6 py-12">
            <div className="w-full max-w-4xl">
                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">
                    Parlons de vos outils d'enrichissement
                </h1>
                <p className="text-gray-500 mb-10 text-center max-w-2xl mx-auto">
                    Quels outils utilisez-vous pour enrichir vos données de prospection ?
                </p>

                {/* Enrichment Tools */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full mb-10">
                {enrichmentTools.map((tool) => {
                    const isSelected = selectedEnrichment.includes(tool.id);
                    
                    return (
                        <button
                            key={tool.id}
                            onClick={() => toggleEnrichment(tool.id)}
                            className={`p-4 rounded-xl border transition-all text-center hover:shadow-sm relative ${
                                isSelected
                                    ? "border-orange-400 bg-orange-50/50"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                        >
                            {isSelected && (
                                <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-orange-500" />
                            )}
                            <p className={`font-medium ${
                                isSelected ? "text-orange-700" : "text-gray-900"
                            }`}>
                                {tool.label}
                            </p>
                        </button>
                    );
                })}
            </div>

                {/* Database Type Section */}
                <div className="w-full mb-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
                        Type de base de données utilisée
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Dynamic Database */}
                        <button
                            onClick={() => setDatabaseType("dynamic")}
                            className={`p-6 rounded-2xl border transition-all text-left hover:shadow-md ${
                                databaseType === "dynamic"
                                    ? "border-orange-400 bg-orange-50/50 shadow-sm"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                        >
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${
                                databaseType === "dynamic" ? "bg-gradient-to-br from-orange-400 to-orange-500" : "bg-gray-100"
                            }`}>
                                <ArrowPathIcon className={`h-6 w-6 ${
                                    databaseType === "dynamic" ? "text-white" : "text-gray-500"
                                }`} />
                            </div>
                            <div className="flex-1">
                                <h3 className={`font-semibold mb-2 ${
                                    databaseType === "dynamic" ? "text-orange-700" : "text-gray-900"
                                }`}>
                                    Base de données dynamique
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Mise à jour automatique des données, enrichissement en temps réel
                                </p>
                            </div>
                        </div>
                    </button>

                        {/* Static Database */}
                        <button
                            onClick={() => setDatabaseType("static")}
                            className={`p-6 rounded-2xl border transition-all text-left hover:shadow-md ${
                                databaseType === "static"
                                    ? "border-orange-400 bg-orange-50/50 shadow-sm"
                                    : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                        >
                        <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${
                                databaseType === "static" ? "bg-gradient-to-br from-orange-400 to-orange-500" : "bg-gray-100"
                            }`}>
                                <CircleStackIcon className={`h-6 w-6 ${
                                    databaseType === "static" ? "text-white" : "text-gray-500"
                                }`} />
                            </div>
                            <div className="flex-1">
                                <h3 className={`font-semibold mb-2 ${
                                    databaseType === "static" ? "text-orange-700" : "text-gray-900"
                                }`}>
                                    Base de données statique
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Fichiers Excel/CSV, données mises à jour manuellement
                                </p>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>

            {/* Bottom Navigation */}
            <div className="w-full max-w-2xl">
                <div className="flex gap-4">
                    <button
                        className="px-6 py-4 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all"
                        onClick={onPrev}
                    >
                        Précédent
                    </button>
                    <button
                        className={`flex-1 px-8 py-4 font-semibold rounded-xl transition-all text-lg ${
                            selectedEnrichment.length > 0 && databaseType
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-orange-700"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                        onClick={handleContinue}
                        disabled={!(selectedEnrichment.length > 0 && databaseType)}
                    >
                        Continuer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardingWebsite;
